import { action, mutator, orchestrator } from 'satcheljs';
import getAppHostStore from '../store/appHostStore';
import { log } from '../utils/telemetry';
import type AppHostViewState from '../store/schema/AppHostViewState';
import type { MailboxInfo } from 'owa-client-types';
import { getModuleContextMailboxInfo } from 'owa-module-context-mailboxinfo';
import { getLogAppId } from 'owa-m365-acquisitions/lib/utils/getLogAppId';
import { trace } from 'owa-trace';

export type OpenMetaOsAppAction = {
    viewState: AppHostViewState;
    mailboxInfo?: MailboxInfo;
};

/* Do not call this action directly, use utils/openLaunchPageMetaOsApp instead */
export interface OnOpenMetaOsAppActionMessage {
    viewState: AppHostViewState;
    mailboxInfo: MailboxInfo | undefined;
}
const onOpenMetaOsApp: ({
    viewState,
    mailboxInfo,
}: OpenMetaOsAppAction) => OnOpenMetaOsAppActionMessage = action(
    'onOpenMetaOsApp',
    ({ viewState, mailboxInfo }: OpenMetaOsAppAction): OnOpenMetaOsAppActionMessage => ({
        viewState,
        mailboxInfo,
    })
);

mutator(onOpenMetaOsApp, (actionMessage: OpenMetaOsAppAction) => {
    let { viewState } = actionMessage;
    const store = getAppHostStore();

    // Only one app is hosted in the main window at a time; drop the previous primary's viewstate.
    const previousPrimaryId = store.primaryAppHostId;

    store.viewStates.set(viewState.appHostId, viewState);
    viewState = store.viewStates.get(viewState.appHostId) ?? viewState;

    if (previousPrimaryId !== undefined && previousPrimaryId !== viewState.appHostId) {
        store.viewStates.delete(previousPrimaryId);
        trace.info(
            `[appSwitch] previousAppHostId=${previousPrimaryId} newAppHostId=${viewState.appHostId} viewStatesCount=${store.viewStates.size}`,
            'metaos'
        );
    }

    store.primaryAppHostId = viewState.appHostId;

    const { appDefinition } = viewState;
    if (!appDefinition || !appDefinition.staticTabs || appDefinition.staticTabs.length === 0) {
        viewState.isInError = true;
    }
});

orchestrator(onOpenMetaOsApp, async actionMessage => {
    const { viewState, mailboxInfo } = actionMessage;
    const { callerFn, subEntityId, appSessionId, appId } = viewState;
    const hasSubEntityId = !!subEntityId;
    const resolvedMailboxInfo = mailboxInfo ?? getModuleContextMailboxInfo();
    // Scrub the app id to a PII-safe value at log time (getLogAppId is async, cache-only).
    const logAppId = appId ? await getLogAppId(appId) : '';
    trace.info(
        `[attempt] mode=open appId=${logAppId} appSessionId=${appSessionId} hasSubEntityId=${hasSubEntityId} callerFn=${callerFn} viewStatesCount=${
            getAppHostStore().viewStates.size
        }`,
        'metaos'
    );
    log(
        {
            callerFn,
            hasSubEntityId,
            appId: logAppId,
            appSessionId,
        },
        resolvedMailboxInfo
    );
});

export default onOpenMetaOsApp;
